import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  CURRENT_TERMS_VERSION,
  CURRENT_PRIVACY_VERSION,
  needsLegalReconsent,
} from "@/lib/legal";

/**
 * One-time, non-blocking toast asking existing logged-in users
 * to acknowledge the current Terms of Service and Privacy Policy.
 * Shown once per session per user until they click "I agree".
 */
export const LegalConsentToast = () => {
  const shownForUserRef = useRef<string | null>(null);

  useEffect(() => {
    const maybeShow = async (uid: string | null) => {
      if (!uid) return;
      if (shownForUserRef.current === uid) return;

      const { data } = await supabase
        .from("profiles")
        .select("terms_version, privacy_version")
        .eq("id", uid)
        .maybeSingle();

      if (!needsLegalReconsent(data)) return;

      shownForUserRef.current = uid;

      const toastId = toast(
        "We've updated our Terms & Privacy Policy",
        {
          description:
            "By continuing to use GamDucks, you agree to our updated Terms of Service and Privacy Policy.",
          duration: Infinity,
          action: {
            label: "I agree",
            onClick: async () => {
              await supabase
                .from("profiles")
                .update({
                  terms_version: CURRENT_TERMS_VERSION,
                  privacy_version: CURRENT_PRIVACY_VERSION,
                  terms_accepted_at: new Date().toISOString(),
                })
                .eq("id", uid);
              toast.dismiss(toastId);
              toast.success("Thanks — your acknowledgement was saved.");
            },
          },
          cancel: {
            label: "Review",
            onClick: () => {
              window.open("/terms", "_blank", "noopener,noreferrer");
            },
          },
        }
      );
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      maybeShow(session?.user?.id ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        maybeShow(session?.user?.id ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return null;
};

export default LegalConsentToast;